<template>
	<div class="range-date-picker">
		<v-menu v-model="menu" class="range-date-picker__menu" v-bind="menuProps" location="bottom start" :close-on-content-click="false" transition="scale-transition" offset-y>
			<template #activator="{ props: activatorProps }">
				<v-text-field :label="label" :model-value="rangeLabel" readonly v-bind="activatorProps" variant="outlined" density="compact" hide-details prepend-inner-icon="event" />
			</template>
			<v-card>
				<div class="d-flex range-date-picker__body" :class="{ 'range-date-picker__body--calendar-open': showCalendar }">
					<v-list v-if="presets.length" class="range-date-picker__presets" density="compact" nav>
						<v-list-item v-for="item in presets" :key="item.value" :title="item.title" :active="activePreset === item.value" @click="applyPreset(item)"></v-list-item>
					</v-list>
					<template v-if="showCalendar">
						<v-divider v-if="presets.length" vertical></v-divider>
						<v-date-picker v-model="dateRange" multiple="range" density="compact" color="primary" :min="minDate" :max="effectiveMaxDate" first-day-of-week="1" show-adjacent-months hide-header></v-date-picker>
					</template>
				</div>
				<slot name="footer"></slot>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn variant="text" color="primary" @click="menu = false">{{ t('Close') }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-menu>

		<v-snackbar v-model="showEndDateHint" :timeout="3000" rounded="xl" color="info">{{ t('PleaseSelectEndDate') }}</v-snackbar>
	</div>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { overlayHandler } from '@/utils/ui/overlayHandler';

const props = defineProps({
	modelValue: { type: Array, default: () => [] },
	presets: { type: Array, default: () => [] },
	label: { type: String, default: '' },
	minDate: { type: [Date, String, Number], default: undefined },
	maxDate: { type: [Date, String, Number], default: undefined },
});
const emit = defineEmits(['update:modelValue']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);

const menu = ref(false);
const calendarToggled = ref(false);
const showEndDateHint = ref(false);
const dateRange = ref(Array.isArray(props.modelValue) ? [...props.modelValue] : []);

// Presets without a 'custom' entry have nowhere to toggle the calendar from, so just always show it.
const hasCustomPreset = computed(() => props.presets.some((item) => item.value === 'custom'));
const showCalendar = computed(() => !hasCustomPreset.value || calendarToggled.value);

const menuProps = computed(() => ({
	attach: overlayTarget || undefined,
	scrim: true,
	scrollStrategy: 'close',
}));

watch(menu, (isOpen) => {
	overlayHandler(menu);
	// Always reopen on the presets-only view, never mid-calendar.
	if (!isOpen) calendarToggled.value = false;
});

watch(
	() => props.modelValue,
	(val) => {
		if (Array.isArray(val) && val !== dateRange.value) dateRange.value = val;
	}
);

watch(dateRange, (val) => {
	emit('update:modelValue', val);
	// Vuetify's range model has length 1 right after the start date, before the end date is picked.
	if (hasCustomPreset.value) showEndDateHint.value = calendarToggled.value && val.length === 1;
});

const rangeStart = computed(() => dateRange.value?.[0] ?? null);
const rangeEnd = computed(() => dateRange.value?.[dateRange.value.length - 1] ?? null);
const rangeLabel = computed(() => {
	if (!rangeStart.value) return '';
	return `${formatDate(rangeStart.value)} → ${formatDate(rangeEnd.value)}`;
});

function dayValue(date) {
	if (!date) return null;
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d.getTime();
}

const activePreset = computed(() => {
	if (!rangeStart.value) return null;
	const startKey = dayValue(rangeStart.value);
	const endKey = dayValue(rangeEnd.value);
	for (const item of props.presets) {
		if (!item.range) continue;
		if (dayValue(item.range[0]) === startKey && dayValue(item.range[1]) === endKey) return item.value;
	}
	return calendarToggled.value ? 'custom' : null;
});

function applyPreset(item) {
	if (item.value === 'custom') {
		calendarToggled.value = true;
		return;
	}
	if (item.range) dateRange.value = item.range;
	menu.value = false;
}

function endOfToday() {
	const d = new Date();
	d.setHours(23, 59, 59, 999);
	return d;
}

// Dates never block except this one: the picker can never select into the future.
const effectiveMaxDate = computed(() => {
	const cap = endOfToday();
	if (props.maxDate == null) return cap;
	const provided = new Date(props.maxDate);
	return provided < cap ? provided : cap;
});

function formatDate(dateStr) {
	if (!dateStr) return '';
	return new Date(dateStr).toLocaleDateString();
}
</script>
